// Settings S5b — tenant user-management HTTP client. D-ORG-3: relocated to
// libs/org-admin-web (Organization Administration owns the user directory,
// invitations, membership and role assignment); the ATS-side people-directory
// reads (assignable users + name resolution) stayed in apps/ats-web/src/users/
// users-api.ts — product-owned, never duplicated here.
//
// Wires the existing endpoints (S5-BE1 + S3a + S3b); S5b adds no
// backend route. Plus a courtesy probe of the financials toggle via
// the S1 GET /v1/tenant/settings — ruling 4 lets a 403 fall through
// gracefully (a pure user-manage admin without tenant:admin:settings
// scope cannot read the setting; the BE rejection is the source of
// truth).

import { ApiError, apiClient } from '@aramo/fe-foundation';

import { fetchRolesCatalog } from '../roles/roles-catalog-api';

import type {
  AssignRolesRequest,
  AssignRolesResponse,
  DisableResponse,
  InviteRequest,
  InviteResponse,
  TenantRoleCatalogEntry,
  TenantUserListView,
  TenantUserView,
} from './types';

export const USERS_PATH = '/v1/tenant/users';
export const SETTINGS_PATH = '/v1/tenant/settings';

// Settings Rebuild D5 — the RolePicker's role list, sourced from the backend
// roles-catalog (closes the hand-mirror drift). Maps the catalog view to the
// picker's row shape.
export async function fetchPickerRoles(): Promise<readonly TenantRoleCatalogEntry[]> {
  const roles = await fetchRolesCatalog();
  return roles.map((r) => ({
    key: r.key,
    label: r.display,
    description: r.description,
    ...(r.requires_setting
      ? {
          helper: r.requires_setting.disabled_message,
          requiresSetting: {
            key: r.requires_setting.setting_key,
            disabledMessage: r.requires_setting.disabled_message,
          },
        }
      : {}),
  }));
}

// GET /v1/tenant/users — the roster.
export async function fetchTenantUsers(): Promise<TenantUserListView> {
  return apiClient.get<TenantUserListView>(USERS_PATH);
}

// GET /v1/tenant/users/:user_id — single row (currently unused in S5b's
// list-centric layout, but exported for spec coverage + future detail-
// view consumers).
export async function fetchTenantUser(userId: string): Promise<TenantUserView> {
  return apiClient.get<TenantUserView>(
    `${USERS_PATH}/${encodeURIComponent(userId)}`,
  );
}

// POST /v1/tenant/users/invitations — invite a new tenant user.
export async function inviteTenantUser(
  body: InviteRequest,
): Promise<InviteResponse> {
  return apiClient.post<InviteResponse>(`${USERS_PATH}/invitations`, body);
}

// POST /v1/tenant/users/:user_id/disable — soft-disable a tenant user.
// The optional `reason` rides the body; the BE accepts an absent body.
export async function disableTenantUser(args: {
  userId: string;
  reason: string | null;
}): Promise<DisableResponse> {
  const body = args.reason !== null ? { reason: args.reason } : {};
  return apiClient.post<DisableResponse>(
    `${USERS_PATH}/${encodeURIComponent(args.userId)}/disable`,
    body,
  );
}

// Invite-S3 (§4.1) — POST /v1/tenant/users/:user_id/enable. Re-enable a
// soft-disabled (INACTIVE) membership; invite_status untouched.
export async function enableTenantUser(
  userId: string,
): Promise<{ membership_id: string }> {
  return apiClient.post<{ membership_id: string }>(
    `${USERS_PATH}/${encodeURIComponent(userId)}/enable`,
  );
}

// Invite-S3 (§4.2) — POST /v1/tenant/users/:user_id/revoke. Cancel a pending
// invite (→ the membership projects to INACTIVE).
export async function revokeTenantInvitation(
  userId: string,
): Promise<{ revoked: boolean; displayed_status: string }> {
  return apiClient.post<{ revoked: boolean; displayed_status: string }>(
    `${USERS_PATH}/${encodeURIComponent(userId)}/revoke`,
  );
}

// Invite-S3 (§4.3) — POST /v1/tenant/users/:user_id/resend. State-dependent:
// INVITED/FAILED → invitation email (fresh token); ACCEPTED → confirmation.
export async function resendTenantInvitation(
  userId: string,
): Promise<{ sent: 'invitation' | 'confirmation' }> {
  return apiClient.post<{ sent: 'invitation' | 'confirmation' }>(
    `${USERS_PATH}/${encodeURIComponent(userId)}/resend`,
  );
}

// Invite-S3 (§4.4) — PATCH /v1/tenant/users/:user_id/email. FAILED-only;
// changes the email then re-issues a fresh invitation.
export async function editTenantUserEmail(args: {
  userId: string;
  email: string;
}): Promise<{ sent: 'invitation' }> {
  return apiClient.patch<{ sent: 'invitation' }>(
    `${USERS_PATH}/${encodeURIComponent(args.userId)}/email`,
    { email: args.email },
  );
}

// PATCH /v1/tenant/users/:user_id/roles — replace the role-set.
export async function assignTenantUserRoles(args: {
  userId: string;
  body: AssignRolesRequest;
}): Promise<AssignRolesResponse> {
  return apiClient.patch<AssignRolesResponse>(
    `${USERS_PATH}/${encodeURIComponent(args.userId)}/roles`,
    args.body,
  );
}

// Aramo-Sign-Mobile-Amendment-M-A8 §2.3 — DELETE /v1/tenant/users/:user_id/products/sign.
// Removes only the member's Aramo Sign roles and signs them out of Aramo Sign; the
// membership, other app roles and every envelope / agreement are kept.
export interface RemoveSignAccessResponse {
  readonly membership_id: string;
  readonly product: 'sign';
  readonly removed_role_keys: readonly string[];
  readonly after_role_keys: readonly string[];
  readonly refresh_tokens_revoked: number;
  readonly device_sessions_revoked: number;
  readonly changed: boolean;
}

export async function removeTenantUserSignAccess(userId: string): Promise<RemoveSignAccessResponse> {
  return apiClient.delete<RemoveSignAccessResponse>(
    `${USERS_PATH}/${encodeURIComponent(userId)}/products/sign`,
  );
}

// Settings S4 — the courtesy probe of the financials toggle (ruling 4).
//
// Try GET /v1/tenant/settings. Two graceful outcomes:
//   - 200 → return the toggle's value (true/false).
//   - 403 → return `unknown` (the caller is a pure user-manage admin
//     without tenant:admin:settings; the proactive-disable falls back
//     to "always show + rely on BE rejection").
//
// Any OTHER error rethrows; this probe is intentionally tolerant of
// 403 only — a 500 here is a real failure the caller surfaces, not a
// reason to silently mis-render the picker.
//
// Return shape:
//   { state: 'known'; enabled: boolean }  — proactive-disable applies.
//   { state: 'unknown' }                  — proactive-disable suppressed;
//                                           BE rejection is the floor.
export type FinancialsToggleState =
  | { state: 'known'; enabled: boolean }
  | { state: 'unknown' };

interface MinimalSettingsView {
  readonly 'audit.financials_enabled'?: boolean;
}

export async function probeFinancialsToggle(): Promise<FinancialsToggleState> {
  try {
    const view = await apiClient.get<MinimalSettingsView>(SETTINGS_PATH);
    const enabled = view['audit.financials_enabled'];
    if (typeof enabled !== 'boolean') {
      // A future backend that drops the key — fall back to unknown.
      return { state: 'unknown' };
    }
    return { state: 'known', enabled };
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 403) {
      return { state: 'unknown' };
    }
    throw err;
  }
}

// §5 Auth-Hardening D4c — the admin-gated probeUserRoster (the S5c shared
// roster probe over GET /v1/tenant/users) is RETIRED. The ATS pickers source
// fetchAssignableUsers and name display sources resolveUserNames — both in
// apps/ats-web/src/users/users-api.ts (product-owned reads).
